// Pure, deterministic helpers for the office quote calculator. These are the
// only pricing-related pieces that live on the frontend: the authoritative
// pricing engine stays on the backend (server/src/modules/pricing) and the
// calculator reads every price from POST /api/quotes/preview, so the displayed
// numbers can never diverge from what POST /api/quotes persists. What lives here
// is just the input-shaping and display derivations the UI needs -- mapping a
// year of manufacture to the engine's age band, turning an annual figure into a
// per-visit one, and the plain-English frequency/discount labels. Pure and
// node-testable (src/lib/quotePricing.test.ts).

// Age bands (V1 thresholds, restored by Director correction 10 July 2026): 0-15 no
// uplift, 16-25 +10%, over 25 +20%. The client only ever DERIVES the current bands
// (ageBandFromYear); the legacy ACP-era bands ("0-20", "21-25") live only in payloads
// stored server-side during the hours the ACP thresholds were live.
export type AgeBand = "0-15" | "16-25" | "25+";
export type QuoteFrequency = "monthly" | "bi_monthly" | "quarterly" | "six_monthly" | "annual";
export type QuoteDuration = "standard" | "extended" | "long";

// Visit-duration options for the calculator select. The multiplier is applied by
// the locked backend engine (Standard 1.0 / Extended 1.5 / Long 2.0); the label
// just states it for the office.
export const DURATION_OPTIONS: { value: QuoteDuration; label: string }[] = [
  { value: "standard", label: "Standard (x1.0)" },
  { value: "extended", label: "Extended (x1.5)" },
  { value: "long", label: "Long (x2.0)" },
];

// Coverage-preview breakdown labels (Manage Lifts pricing breakdown). The multiplier NUMBERS
// themselves are never hardcoded here: the API always returns the exact figure it billed
// (durationMult / ageMult on each breakdown row), so these constants only supply the static band
// name and range text next to that number. A client label can therefore never disagree with what
// was actually charged, only ever restate it.
export const DURATION_BAND_NAME: Record<QuoteDuration, string> = {
  standard: "Standard",
  extended: "Extended",
  long: "Long",
};
export const DURATION_BAND_RANGE: Record<QuoteDuration, string> = {
  standard: "1-6 stops",
  extended: "7-11 stops",
  long: "12+ stops",
};
export const AGE_BAND_LABEL: Record<AgeBand, string> = {
  "0-15": "0-15yr",
  "16-25": "16-25yr",
  "25+": "25+yr",
};

// Formats a price multiplier for display: 1 decimal place normally (1.0, 1.5, 2.0), stepping up to
// 2 only when 1 would actually lose a fractional part (1.05 -> "1.05", not "1.1"). Comparing via
// toFixed round-trip rather than a raw *10/Math.round check, since the latter is exactly the kind
// of naive floating-point arithmetic that misbehaves on values like 1.1.
export function formatPriceMult(n: number): string {
  const oneDecimal = n.toFixed(1);
  if (Math.abs(Number(oneDecimal) - n) < 1e-9) return oneDecimal;
  return n.toFixed(2);
}

// Floor count determines the recommended service duration (Director-locked 25 June 2026;
// threshold revised 3 July 2026, Session AAG): 1 to 6 floors Standard (x1.0), 7 to 11 floors
// Extended (x1.5), 12+ floors Long (x2.0). A non-positive, missing or non-finite floor count
// defaults to Standard (the safe baseline). The calculator auto-applies this on EVERY floor-count
// change, so the duration tracks the building height without manual selection; a manual pick is
// respected until the floor count changes again, which hands the rule back. (Session AHA corrected
// this comment: it used to say the auto-apply was skipped once the office had picked a duration,
// which the stops handler has never done and which the locked rule does not ask for.)
export function durationForFloors(floors: number | null | undefined): QuoteDuration {
  if (floors == null || !Number.isFinite(floors) || floors <= 6) return "standard";
  if (floors <= 11) return "extended";
  return "long";
}

// The office-facing explanation of the rule above (Session AHA). The duration is auto-applied from
// the stop count and carries a real price multiplier, so the calculator states WHY it reads as it
// does rather than changing a priced field silently. Returns null from 1 to 6 stops, where the rule
// gives Standard (x1.0) and there is nothing to explain.
//
// It also reports whether the CURRENT duration still matches the rule, IN BOTH DIRECTIONS. A manual
// pick is respected (see above), so the office can sit on a duration the stop count does not imply,
// and either direction moves real money: Standard on a 12-stop lift halves the visit multiplier
// (under-charging), and Long on a 4-stop lift doubles it (over-charging a customer, the larger
// delta of the two). An earlier cut of this returned null for the whole 1 to 6 band, which left
// that doubling as the one silent case; the rule has an opinion at every stop count, so the mismatch
// is now stated wherever it exists. It costs one short line and changes nothing.
export function durationHintForStops(
  stops: number | null | undefined,
  duration: QuoteDuration,
): { text: string; matchesRule: boolean } | null {
  if (stops == null || !Number.isFinite(stops)) return null;
  const suggested = durationForFloors(stops);
  const matchesRule = duration === suggested;
  // The one silent case: the rule gives the default AND the lift is on it, so there is no
  // multiplier to explain and no divergence to report.
  if (matchesRule && suggested === "standard") return null;
  // Reuse the select's own labels so the stated multipliers can never drift from the ones on screen.
  const labelFor = (d: QuoteDuration) => DURATION_OPTIONS.find((o) => o.value === d)?.label ?? d;
  return {
    matchesRule,
    text: matchesRule
      ? `Set from ${stops} stops: ${labelFor(suggested)}.`
      : // Name the value actually in the field. Without it the reader has to diff the sentence
        // against the select 4px above to notice they disagree at all.
        `${stops} stops suggests ${labelFor(suggested)}, not ${labelFor(duration)}.`,
  };
}

// Postcode surcharge detection, mirrored from the server single source of truth
// (server/src/modules/quotes/pricing.ts, Session AAE) the same way the annual-frequency
// guard is mirrored: the server prices authoritatively; this copy only drives the LIVE
// notice under the calculator's postcode field (per keystroke, no round-trip). A parity
// test asserts the lists and rates stay identical to the server's
// (src/lib/quotePricing.surcharges.test.ts). CCZ congestion charge £18/visit, Westminster
// and K&C parking £20/visit; a postcode can attract BOTH (all of W1, SW1, WC1, WC2).
// Matched on the FULL OUTWARD CODE, case-insensitively; a bare district that is not
// itself a complete outward code ("EC1", "SW1") deliberately does not match.
export const CCZ_PER_VISIT = 18;
export const WESTMINSTER_KC_PER_VISIT = 20;
export const CCZ_OUTWARD_CODES: ReadonlySet<string> = new Set([
  "EC1A", "EC1M", "EC1N", "EC1R", "EC1V", "EC1Y",
  "EC2A", "EC2M", "EC2N", "EC2R", "EC2V", "EC2Y",
  "EC3A", "EC3M", "EC3N", "EC3R", "EC3V",
  "EC4A", "EC4M", "EC4N", "EC4R", "EC4V", "EC4Y",
  "WC1A", "WC1B", "WC1E", "WC1H", "WC1N", "WC1R", "WC1V", "WC1X",
  "WC2A", "WC2B", "WC2E", "WC2H", "WC2N", "WC2R",
  "W1A", "W1B", "W1C", "W1D", "W1F", "W1G", "W1H", "W1J", "W1K", "W1S", "W1T", "W1U", "W1W",
  "SW1A", "SW1E", "SW1H", "SW1P", "SW1V", "SW1W", "SW1X", "SW1Y",
  "N1C", "N1P", "SE1P",
]);
export const WESTMINSTER_KC_OUTWARD_CODES: ReadonlySet<string> = new Set([
  "W1A", "W1B", "W1C", "W1D", "W1F", "W1G", "W1H", "W1J", "W1K", "W1S", "W1T", "W1U", "W1W",
  "SW1A", "SW1E", "SW1H", "SW1P", "SW1V", "SW1W", "SW1X", "SW1Y",
  "WC1A", "WC1B", "WC1E", "WC1H", "WC1N", "WC1R", "WC1V", "WC1X",
  "WC2A", "WC2B", "WC2E", "WC2H", "WC2N", "WC2R",
  "W2", "W8", "W11", "W14",
  "SW3", "SW5", "SW7", "SW10",
]);
// True when the input looks like a complete UK postcode (so the calculator only calls the free
// postcode-lookup provider on a full postcode, never a half-typed one). Mirrors the server regex
// (server/src/integrations/address/postcodesIo.ts looksLikeFullPostcode); postcodes.io is the real
// validator, this only avoids pointless calls.
export function looksLikeFullPostcode(raw: string | null | undefined): boolean {
  return !!raw && /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(raw.trim());
}

// A plain-English one-line summary of a resolved postcode's geography (town, district, region), for
// the calculator's green "postcode recognised" confirmation. Drops blanks and consecutive duplicates
// (the free provider's town can equal its district), so "West End / Westminster / London" reads
// cleanly and "Westminster / Westminster / London" collapses. Pure + tested.
export function placeSummary(place: { town?: string | null; district?: string | null; region?: string | null } | null | undefined): string {
  if (!place) return "";
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const p of [place.town, place.district, place.region]) {
    const v = (p ?? "").trim();
    if (v && !seen.has(v.toLowerCase())) {
      seen.add(v.toLowerCase());
      parts.push(v);
    }
  }
  return parts.join(", ");
}

export function postcodeOutward(postcode: string | null | undefined): string | null {
  if (!postcode) return null;
  const pc = postcode.trim().toUpperCase();
  if (!pc) return null;
  if (/\s/.test(pc)) return pc.split(/\s+/)[0]!;
  const m = pc.match(/^(.+?)(\d[A-Z]{2})$/);
  return m ? m[1]! : pc;
}
export interface PostcodeSurcharges {
  ccz: boolean;
  westminsterParking: boolean;
  cczPerVisit: number;
  parkingPerVisit: number;
}
export function surchargesForPostcode(postcode: string | null | undefined): PostcodeSurcharges {
  const outward = postcodeOutward(postcode);
  return {
    ccz: outward != null && CCZ_OUTWARD_CODES.has(outward),
    westminsterParking: outward != null && WESTMINSTER_KC_OUTWARD_CODES.has(outward),
    cczPerVisit: CCZ_PER_VISIT,
    parkingPerVisit: WESTMINSTER_KC_PER_VISIT,
  };
}

// Extract a UK postcode from free-text (typically a site address ending in the postcode),
// normalised as "OUTWARD INWARD" (e.g. "W1K 2AZ"), or null when none is present. The LAST
// match wins (an address ends with its postcode). Mirrors the server
// (server/src/modules/quotes/pricing.ts postcodeFromAddress) so the calculator can recover the
// postcode the office typed into the address line and left out of the dedicated field (Session
// AAG). Parity-tested against the server (src/lib/quotePricing.surcharges.test.ts).
export function postcodeFromAddress(address: string | null | undefined): string | null {
  if (!address) return null;
  const matches = [...address.toUpperCase().matchAll(/([A-Z]{1,2}\d[A-Z\d]?)\s*(\d[A-Z]{2})/g)];
  if (matches.length === 0) return null;
  const last = matches[matches.length - 1]!;
  return `${last[1]} ${last[2]}`;
}

// The plain-English live notice for the calculator's postcode field. Null when the
// postcode attracts no surcharge (no notice shown).
export function surchargeNoticeForPostcode(postcode: string | null | undefined): string | null {
  const s = surchargesForPostcode(postcode);
  if (s.ccz && s.westminsterParking) {
    return `This postcode attracts a Congestion Charge Zone surcharge of £${s.cczPerVisit}/visit and a Westminster/K&C parking surcharge of £${s.parkingPerVisit}/visit. These are added to the annual price above.`;
  }
  if (s.ccz) {
    return `This postcode attracts a Congestion Charge Zone surcharge of £${s.cczPerVisit}/visit. This is added to the annual price above.`;
  }
  if (s.westminsterParking) {
    return `This postcode attracts a Westminster/K&C parking surcharge of £${s.parkingPerVisit}/visit. This is added to the annual price above.`;
  }
  return null;
}

// Manufacturer suggestions for the datalist (major UK lift brands, Director-specified order, Session
// P). The field is a datalist, NOT a select: any value not on the list is accepted and persists (free
// text). Not priced -- recorded on the quote and pre-populates the Asset on acceptance.
export const MANUFACTURER_SUGGESTIONS = [
  "Otis",
  "KONE",
  "Schindler",
  "TK Elevator",
  "Hammond and Champness",
  "Stannah",
  "Pickerings",
  "Evans Lifts",
  "Express Lifts",
  "Marryat and Scott",
  "MovemanSKG",
  "Hydratec",
  "Jackson Lifts",
  "Orona",
  "Cibes",
  "Terry Lifts",
  "Aritco",
];

// The full lift-type list for the calculator dropdown, in plain English. The values
// are the engine keys (the server validates them and prices via the locked engine).
// "custom" reveals a user multiplier. Care-home equipment (hoists, stand aids, tables)
// is priced only through the dedicated Care Home Equipment section
// (CARE_HOME_EQUIPMENT_PRESETS), never as a lift type; the legacy care-equipment
// lift-type multipliers (ceilingHoist / standAid / treatmentTable) were retired in
// Session AFO after a production audit found no stored quote used them.
export const LIFT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "passengerTrall", label: "Passenger (traction MRL)" },
  { value: "passengerTramr", label: "Passenger (traction, machine room)" },
  { value: "passengerHydr", label: "Passenger (hydraulic)" },
  { value: "goodsTraction", label: "Goods (traction)" },
  { value: "goodsHydr", label: "Goods (hydraulic)" },
  { value: "goodsTractionMrl", label: "Goods (traction MRL)" },
  { value: "platform", label: "Platform / disabled access" },
  { value: "stepLift", label: "Step lift" },
  { value: "throughFloor", label: "Through-floor (home lift)" },
  { value: "serviceLift", label: "Service / laundry lift" },
  { value: "trolleyLift", label: "Trolley lift" },
  { value: "dumbwaiter", label: "Dumbwaiter" },
  { value: "stairlift", label: "Stairlift (straight)" },
  { value: "stairliftCurved", label: "Stairlift (curved)" },
  { value: "scissorSmall", label: "Scissor lift (kitchen / small)" },
  { value: "scissorGoods", label: "Scissor lift (goods / large)" },
  { value: "heavyDuty", label: "Heavy duty / car lift" },
  { value: "custom", label: "Custom / Other" },
];

export function isCustomKey(liftType: string): boolean {
  return liftType === "custom";
}

// A preset line item: a description plus a default annual price the office can pick
// from a menu (then edit). Rows stay fully editable and a custom (blank) row is
// always available, so these are quick-fills, not a closed list. Care-home
// equipment presets additionally carry the item's LOLER minimum frequency
// (minVisitsYr: 2 = six-monthly for people-lifting items, 1 = annual for
// fixed-height items); additional-item presets have none (flat one-off items).
export interface LinePreset {
  description: string;
  pricePerYear: number;
  minVisitsYr?: EquipmentVisits;
}

// Additional-item presets (seed 3.8). Prices carried over verbatim from the V1
// calculator's quick-add menu so the office gets the same pre-filled figures.
// Never multi-lift-discounted; shown as a separate section on the quote.
export const ADDITIONAL_ITEM_PRESETS: LinePreset[] = [
  { description: "Electric roller shutter (standard)", pricePerYear: 200 },
  { description: "Electric roller shutter (fire-rated)", pricePerYear: 280 },
  { description: "Electric roller shutter (large/industrial)", pricePerYear: 350 },
  { description: "Automatic sliding doors (single set)", pricePerYear: 320 },
  { description: "Automatic sliding doors (double set)", pricePerYear: 480 },
  { description: "Automatic swing door", pricePerYear: 250 },
  { description: "Manual roller shutter", pricePerYear: 120 },
  { description: "Automatic gate / barrier", pricePerYear: 280 },
  { description: "Traffic light system", pricePerYear: 75 },
];

// Care-home equipment presets (seed 3.9; frequency model per the ACP care equipment
// spec, Director-approved 10 July 2026). Maintenance only, qty-driven, never
// multi-lift-discounted; shown as a separate section on the quote. The price is the
// CATALOGUE price: the yearly price at the item's LOLER minimum frequency. Equipment
// that lifts or raises a PERSON (hoists, stand aids, profiling beds, rise-and-fall
// tables -- spec 2.2c: a patient on a rising bed is being lifted) carries the
// six-monthly LOLER thorough-examination minimum (minVisitsYr 2, so the catalogue
// price buys 2 visits/yr); only genuinely fixed-height items (the nursing chair)
// are annual (minVisitsYr 1). The office can step a row UP to quarterly, never below
// the minimum; added visits are charged at the 85% taper.
export const CARE_HOME_EQUIPMENT_PRESETS: LinePreset[] = [
  { description: "Ceiling hoist (straight track)", pricePerYear: 180, minVisitsYr: 2 },
  { description: "Ceiling hoist (X-Y / full room)", pricePerYear: 250, minVisitsYr: 2 },
  { description: "Ceiling hoist (multi-room system)", pricePerYear: 350, minVisitsYr: 2 },
  { description: "Mobile hoist (floor standing)", pricePerYear: 80, minVisitsYr: 2 },
  { description: "Bath hoist", pricePerYear: 100, minVisitsYr: 2 },
  { description: "Stand aid", pricePerYear: 60, minVisitsYr: 2 },
  // 100 -> 133 by Director decision D5 (Session AIC). D5 as written asked to raise a "table"
  // MULTIPLIER from 0.30 to 0.40, but those multipliers (ceilingHoist / standAid / treatmentTable)
  // were retired in Session AFO after a production audit found no stored quote used them, and two
  // tests now pin their absence. Care equipment prices from this flat catalogue instead, so the
  // Director applied the same +33 percent to the live lever: 100 x 4/3 = 133.
  { description: "Lifting/treatment table", pricePerYear: 133, minVisitsYr: 2 },
  { description: "Electric profiling bed", pricePerYear: 90, minVisitsYr: 2 },
  { description: "Medical / nursing chair", pricePerYear: 75, minVisitsYr: 1 }, // fixed-height: annual minimum
  { description: "Medical bed", pricePerYear: 123, minVisitsYr: 2 }, // Added Session AAI; people-lifting per spec 2.2c (123 = 2 visits at 61.50, commercial check flagged)
];

// ---- Care-equipment frequency model (client mirrors of the server single source,
// server/src/modules/quotes/pricing.ts; parity-tested). Display-only: the server
// prices authoritatively via POST /api/quotes/preview. ----

// The cadences offered for equipment (Director decision 2.2b): yearly (1),
// six-monthly (2), quarterly (4). Never bi-monthly or monthly.
export const EQUIPMENT_VISIT_OPTIONS = [1, 2, 4] as const;
export type EquipmentVisits = (typeof EQUIPMENT_VISIT_OPTIONS)[number];

export const EQUIPMENT_FREQ_LABEL: Record<number, string> = {
  1: "Yearly",
  2: "Six-monthly",
  4: "Quarterly",
};

// The frequency <select> options for an equipment row: every cadence at or above the
// item's LOLER minimum (never below it -- a people-lifting item is never offered
// yearly). Pure.
export function equipmentFrequencyOptionsForMin(minVisitsYr: EquipmentVisits): { value: EquipmentVisits; label: string }[] {
  return EQUIPMENT_VISIT_OPTIONS.filter((v) => v >= minVisitsYr).map((v) => ({ value: v, label: EQUIPMENT_FREQ_LABEL[v]! }));
}

// Session ACV (gates-only tiered contracts): the service-cadence options for a
// TIER-COVERED additional item on a LIFT-LESS quote -- the same yearly / six-monthly /
// quarterly set the equipment uses, with no minimum (gates and doors carry no LOLER
// examination clock). SCHEDULING ONLY: the cadence never changes the price (the entered
// price is the item's annual Core figure); it flows to activation, where the item
// materialises into a scheduled asset at this frequency. Six-monthly is the safe
// default, mirroring the server (ADDITIONAL_SERVICE_DEFAULT_VISITS), parity-tested.
export const ADDITIONAL_SERVICE_DEFAULT_VISITS = 2;
export function additionalServiceFrequencyOptions(): { value: EquipmentVisits; label: string }[] {
  return EQUIPMENT_VISIT_OPTIONS.map((v) => ({ value: v, label: EQUIPMENT_FREQ_LABEL[v]! }));
}

// The tapered annual for ONE unit of an equipment row (mirror of the server
// equipmentUnitAnnual): the catalogue price at the minimum, plus added visits above
// it at 85% of the implied per-visit rate. A row without a positive integer minimum
// is a legacy flat line (price as-is). Exact; display rounds.
export function equipmentUnitAnnual(row: { price: number; minVisitsYr?: number | null; chosenVisitsYr?: number | null }): number {
  const price = Number(row.price);
  if (!Number.isFinite(price) || price < 0) return 0;
  const minRaw = Number(row.minVisitsYr);
  if (!Number.isInteger(minRaw) || minRaw < 1) return price;
  const chosenRaw = Number(row.chosenVisitsYr);
  const chosen = Number.isInteger(chosenRaw) && chosenRaw > minRaw ? chosenRaw : minRaw;
  return (price / minRaw) * taperedVisits(chosen, minRaw);
}

// The row total: unitAnnual x qty (mirror of the server equipmentRowAnnual).
export function equipmentRowAnnual(row: { price: number; qty?: number | null; minVisitsYr?: number | null; chosenVisitsYr?: number | null }): number {
  const qty = Number(row.qty);
  return equipmentUnitAnnual(row) * (Number.isFinite(qty) && qty > 0 ? qty : 1);
}

// EQUIPMENT SINGLE-ITEM SITE MINIMUM (Session ACS item A, Director 10 July 2026) --
// client mirror of the server rule (server/src/modules/quotes/pricing.ts priceQuote),
// parity-tested, display-only (the server prices authoritatively): on an EQUIPMENT-ONLY
// quote (zero lifts) whose PRICED equipment reduces to exactly one row at qty 1 with a
// cadence, the annual floors at visitsYr x the Core visit rate (the anchor, default
// 160) -- a lone visit for one item must cost at least a standard visit. Returns the
// floored figure when the minimum applies AND bites, else null (a lift on the quote,
// qty >= 2, 2+ rows, a legacy flat row, or a computed annual already above the floor).
// Session ACV: a TIER-COVERED additional item disapplies the minimum ONLY when its
// service cadence is at least the equipment's (every equipment visit then coincides
// with a covered visit the item's own price already funds); a SPARSER covered cadence
// leaves lone equipment visits, so the floor still applies. coveredMaxVisitsYr is the
// max serviceVisitsYr across the quote's covered rows (0 when none), mirroring the
// server rule exactly (parity-tested).
export function equipmentSingleItemMinimum(
  numLifts: number,
  rows: { price: number; qty?: number | null; minVisitsYr?: number | null; chosenVisitsYr?: number | null }[],
  anchor = 160,
  coveredMaxVisitsYr = 0,
): { perVisit: number; annual: number } | null {
  if (numLifts !== 0 || rows.length !== 1) return null;
  const row = rows[0]!;
  const qtyN = Number(row.qty);
  const qty = Number.isFinite(qtyN) && qtyN > 0 ? qtyN : 1;
  if (qty !== 1) return null;
  const min = Number(row.minVisitsYr);
  if (!Number.isInteger(min) || min < 1) return null; // legacy flat row: no per-visit basis
  const chosen = Number(row.chosenVisitsYr);
  const visitsYr = Number.isInteger(chosen) && chosen > min ? chosen : min;
  if (coveredMaxVisitsYr >= visitsYr) return null; // every equipment visit rides a covered visit
  const floorAnnual = visitsYr * anchor;
  return equipmentRowAnnual(row) < floorAnnual ? { perVisit: anchor, annual: floorAnnual } : null;
}

// Door-type options (seed 4.1). The `manual` flag is the only thing that affects
// price (a manual door adds a flat +£10 per stop per visit, applied by the engine
// adapter). Auto doors never trigger the fee.
export const DOOR_TYPE_OPTIONS: { value: string; label: string; manual: boolean }[] = [
  { value: "auto_side", label: "Automatic side-opening", manual: false },
  { value: "auto_centre", label: "Automatic centre-opening", manual: false },
  { value: "electric_shutter", label: "Electric shutter (powered)", manual: false },
  { value: "manual_swing", label: "Manual swing", manual: true },
  { value: "bifold", label: "Bi-fold / concertina", manual: true },
  { value: "gate", label: "Collapsible gate", manual: true },
  { value: "roller_goods", label: "Roller shutter (goods)", manual: true },
];

// The door type a fresh lift starts on (the calculator's makeUnit default). Named here so the
// form default and the label inverse below cannot drift apart.
export const DEFAULT_DOOR_TYPE = "auto_side";

export function doorTypeIsManual(value: string): boolean {
  return DOOR_TYPE_OPTIONS.find((d) => d.value === value)?.manual ?? false;
}

// The plain-English door label for a door-type value (for the documentation/PDF
// spec table). Falls back to the value itself for an unknown code.
export function doorTypeLabel(value: string): string {
  return DOOR_TYPE_OPTIONS.find((d) => d.value === value)?.label ?? value;
}

// The INVERSE of doorTypeLabel: recover the door-type VALUE from the label stored on a
// quote's payload lift. A stored lift keeps only the label (`door`) plus the derived
// `manualDoors` boolean, never the option value, so rebuilding the calculator's form state
// from a stored quote (Session AHP) has to map back through the same single source of
// truth. Symmetrical with doorTypeLabel's own fallback: an unrecognised label is returned
// verbatim, so a round-trip through both functions is stable for a known option and lossless
// for an unknown one. A blank/absent label yields the form default rather than an empty
// select value.
export function doorTypeValueFromLabel(label: string | undefined | null): string {
  const l = (label ?? "").trim();
  if (!l) return DEFAULT_DOOR_TYPE;
  return DOOR_TYPE_OPTIONS.find((d) => d.label === l)?.value ?? l;
}

// Capacity suggestions for the calculator datalist (documentation only, never priced;
// carried over verbatim from the V1 calculator). The office can pick one or type a
// custom capacity (e.g. "2 person / 250kg" or "5000kg goods"). Pre-populates the Asset.
export const CAPACITY_SUGGESTIONS = [
  "4 person / 300kg",
  "6 person / 450kg",
  "8 person / 630kg",
  "10 person / 800kg",
  "13 person / 1000kg",
  "16 person / 1275kg",
  "20 person / 1600kg",
  "24 person / 1800kg",
  "33 person / 2500kg",
];

// Controller options (documentation only, never priced; carried over verbatim from the
// V1 calculator). Shown on the quote PDF lift-specification table.
export const CONTROLLER_OPTIONS: { value: string; label: string }[] = [
  { value: "Modern microprocessor / VVVF", label: "Modern microprocessor / VVVF" },
  { value: "Older microprocessor", label: "Older microprocessor" },
  { value: "Relay logic", label: "Relay logic" },
  { value: "Ward Leonard / DC", label: "Ward Leonard / DC" },
];

// Quote-reference auto-generation (seed 3.13): first 3 letters of the site name
// (uppercase, letters only) + "-" + MMYY. Fallback prefix "ELE" when there is no
// usable site name. The server allocates the canonical persisted reference on save;
// this is the office-facing preview shown live as the site name is typed. Pure
// (currentDate passed in, no Date dependency).
export function quoteRefFromSite(siteName: string, now: Date): string {
  const letters = (siteName || "").replace(/[^A-Za-z]/g, "").toUpperCase();
  const prefix = letters.length >= 1 ? letters.slice(0, 3) : "ELE";
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const yy = String(now.getFullYear() % 100).padStart(2, "0");
  return `${prefix}-${mm}${yy}`;
}

// Frequency options for the calculator select, in plain English (the customer
// never sees "six_monthly"). One source of truth for the label and the value.
export const FREQUENCY_OPTIONS: { value: QuoteFrequency; label: string }[] = [
  { value: "monthly", label: "Monthly" },
  { value: "bi_monthly", label: "Every 2 months" },
  { value: "quarterly", label: "Quarterly" },
  { value: "six_monthly", label: "Twice yearly" },
  { value: "annual", label: "Annual" },
];

const FREQUENCY_LABELS: Record<QuoteFrequency, string> = {
  monthly: "Monthly",
  bi_monthly: "Every 2 months",
  quarterly: "Quarterly",
  six_monthly: "Twice yearly",
  annual: "Annual",
};

export function frequencyLabel(freq: QuoteFrequency): string {
  return FREQUENCY_LABELS[freq] ?? "Quarterly";
}

// ---- Contract-floor mirror (Session ACK: the office-only anchor floor note) ----
// Mirrored from the server single source of truth (server/src/modules/pricing/engine.ts +
// quotes/pricing.ts) so the office calculator can show WHICH contract floor applies to the current
// configuration and why an anchor below a threshold has no visible effect (the anchor-independent
// floor protects the contract minimum). OFFICE-ONLY: the server always prices authoritatively; this
// mirror only drives the informational note under the anchor field. A parity suite pins every value
// byte-equal to the server (src/lib/quotePricing.floor.test.ts) so any drift is a red test.

// Frequency -> visits per year (mirror of the server FREQUENCY_VISITS).
export const FREQUENCY_VISITS: Record<QuoteFrequency, number> = {
  monthly: 12,
  bi_monthly: 6,
  quarterly: 4,
  six_monthly: 2,
  annual: 1,
};

export function visitsPerYear(freq: QuoteFrequency): number {
  return FREQUENCY_VISITS[freq] ?? 4;
}

// THE frequency taper (mirror of the server engine's FREQUENCY_TAPER, Director
// repricing 10 July 2026): visits above the baseline cadence are charged at 85% of
// the per-visit rate. Display-only on the client (the office floor note + the
// equipment row annuals); the server always prices authoritatively. Parity-tested.
export const FREQUENCY_TAPER = 0.85;
export const TAPER_BASELINE_VISITS = 4;

// The effective billed visit count for a cadence (mirror of the server
// taperedVisits): visits at or below the baseline count in full; visits above it
// count at FREQUENCY_TAPER each.
export function taperedVisits(visitsYr: number, baselineVisits: number = TAPER_BASELINE_VISITS): number {
  if (!Number.isFinite(visitsYr) || visitsYr <= baselineVisits) return visitsYr;
  return baselineVisits + (visitsYr - baselineVisits) * FREQUENCY_TAPER;
}

// Explicit contract floor by visits/year (mirror of the server CONTRACT_FLOOR_BY_VISITS +
// contractFloor; bi-monthly 912 / monthly 1728 are the TAPERED values, Director
// repricing 10 July 2026). Anchor-independent; applies to non-exempt lift types only.
export const CONTRACT_FLOOR_BY_VISITS: Record<number, number> = { 1: 240, 2: 400, 4: 640, 6: 912, 12: 1728 };

export function contractFloor(visitsYr: number): number {
  return CONTRACT_FLOOR_BY_VISITS[visitsYr] ?? 160 * taperedVisits(visitsYr);
}

// Lift-type multipliers (mirror of the server LIFT_TYPE_MULTIPLIERS). Used ONLY to decide floor
// exemption for the office note; pricing always comes from the server. Parity-tested.
export const LIFT_TYPE_MULTIPLIERS: Record<string, number> = {
  passengerTrall: 1.0,
  passengerTramr: 1.05,
  passengerHydr: 1.0,
  goodsTraction: 1.0,
  goodsHydr: 1.0,
  goodsTractionMrl: 1.0,
  platform: 0.39,
  stepLift: 0.35,
  throughFloor: 0.5,
  serviceLift: 0.75,
  trolleyLift: 0.45,
  dumbwaiter: 0.4,
  stairlift: 0.3,
  stairliftCurved: 0.4,
  scissorSmall: 0.23,
  scissorGoods: 0.45,
  heavyDuty: 1.0,
  custom: 1.0,
};

// A lift type is floor-exempt (has no contract floor) when its multiplier is <= 0.75, with one
// server-matching exception: a `custom` type ALWAYS keeps the floor (the server adapter forces
// floorExempt=false for custom regardless of its multiplier).
// Mirrors the server exempt logic (quotes/pricing.ts priceQuote + engine.ts calculatePrice). Pure.
export function liftTypeIsFloorExempt(liftType: string): boolean {
  if (isCustomKey(liftType)) return false;
  const mult = LIFT_TYPE_MULTIPLIERS[liftType];
  return mult != null && mult <= 0.75;
}

// The office-only anchor floor note data (Session ACK). Given the current lifts' types + frequencies,
// returns the contract floor that applies and the per-visit anchor below which it has no visible
// effect. Null when there are no lifts (no floor to explain). `exempt` when every lift is floor-exempt
// (the anchor scales those prices freely, so there is no floor). Otherwise the floor is taken at the
// shortest cadence across the NON-EXEMPT lifts (the total bills at the shortest frequency, which
// carries the highest annual floor). The component formats this into the one-line note (so the £
// formatting matches the rest of the calculator). Pure and node-testable.
export type AnchorFloorInfo =
  | { kind: "floor"; floor: number; visitsYr: number; perVisitFloor: number }
  | { kind: "exempt" }
  | null;

export function anchorFloorInfo(lifts: { liftType: string; frequency: QuoteFrequency }[]): AnchorFloorInfo {
  if (lifts.length === 0) return null;
  const nonExempt = lifts.filter((l) => !liftTypeIsFloorExempt(l.liftType));
  if (nonExempt.length === 0) return { kind: "exempt" };
  const visitsYr = Math.max(...nonExempt.map((l) => visitsPerYear(l.frequency)));
  const floor = contractFloor(visitsYr);
  // The anchor threshold below which the floor binds: the annual is anchor x
  // taperedVisits (ACP taper), so the per-visit threshold divides by the TAPERED
  // visit count (1728 / 10.8 = 160 monthly), not the raw one. At or below quarterly
  // taperedVisits === visitsYr, so those cadences are unchanged.
  return { kind: "floor", floor, visitsYr, perVisitFloor: Math.round(floor / taperedVisits(visitsYr)) };
}

// ---- Annual-frequency compliance guard (BS EN 81-28 / LOLER) ----
// Annual (1 visit/yr) servicing is permitted on the goods + low-traffic lift types ONLY (the V1
// scope, Session P): goods traction / hydraulic / traction MRL, dumbwaiter, service/laundry lift and
// trolley lift. Every other type (passenger, platform, stairlift, care equipment, custom) needs a
// minimum of twice-yearly servicing. This mirrors the authoritative server guard in
// server/src/modules/quotes/pricing.ts (which enforces it on quote save); the UI side
// disables the Annual option and coerces an now-invalid choice so the office cannot
// pick a non-compliant cadence in the first place.
export const ANNUAL_ALLOWED_LIFT_TYPES = ["goodsTraction", "goodsHydr", "goodsTractionMrl", "dumbwaiter", "serviceLift", "trolleyLift"];

export function liftTypeAllowsAnnual(liftType: string): boolean {
  return ANNUAL_ALLOWED_LIFT_TYPES.includes(liftType);
}

export const ANNUAL_FREQUENCY_BLOCKED_REASON =
  "Annual servicing is permitted on goods, dumbwaiter, service and trolley lifts only (BS EN 81-28 / LOLER). This lift type requires a minimum of twice-yearly servicing.";

export interface FrequencyCheck {
  valid: boolean;
  reason?: string;
}

// Pure compliance check: only an annual cadence on a type outside the allow-list is invalid.
export function checkFrequencyForLiftType(liftType: string, frequency: QuoteFrequency): FrequencyCheck {
  if (frequency === "annual" && !liftTypeAllowsAnnual(liftType)) {
    return { valid: false, reason: ANNUAL_FREQUENCY_BLOCKED_REASON };
  }
  return { valid: true };
}

export interface FrequencyOption {
  value: QuoteFrequency;
  label: string;
  disabled?: boolean;
  title?: string;
}

// Frequency <select> options for a given lift type: the Annual option is disabled
// (with an explanatory tooltip and a label suffix) on any type outside the allow-list, so the
// office sees why it cannot pick it. Allowed goods/low-traffic types get the plain options.
export function frequencyOptionsForType(liftType: string): FrequencyOption[] {
  const annualOk = liftTypeAllowsAnnual(liftType);
  return FREQUENCY_OPTIONS.map((o) =>
    o.value === "annual" && !annualOk
      ? { ...o, label: "Annual (selected lift types)", disabled: true, title: ANNUAL_FREQUENCY_BLOCKED_REASON }
      : { ...o },
  );
}

// When a lift's type changes, drop an now-invalid annual cadence down to the minimum
// compliant frequency (twice yearly), mirroring the V1 calculator's fallback. Returns
// the frequency to keep using.
export function coerceFrequencyForType(liftType: string, frequency: QuoteFrequency): QuoteFrequency {
  return checkFrequencyForLiftType(liftType, frequency).valid ? frequency : "six_monthly";
}

// Map a 4-digit year of manufacture to the engine's age band. The engine bumps
// price by +10% at 16-25 years old and +20% at over 25 (the V1 thresholds, restored
// by Director correction 10 July 2026: an older lift never gets cheaper). A missing,
// malformed, future or pre-1900 year yields null -- the caller then sends no age
// band and the engine applies its default (no bump). The server re-derives the band
// from the year anyway (no client-side pricing); this mirror only keeps the sent
// band honest. currentYear is passed in so this stays pure (no Date dependency).
export function ageBandFromYear(year: number | null | undefined, currentYear: number): AgeBand | null {
  if (year == null || !Number.isInteger(year)) return null;
  if (year < 1900 || year > currentYear) return null;
  const age = currentYear - year;
  if (age <= 15) return "0-15";
  if (age <= 25) return "16-25";
  return "25+";
}

// Parse the optional year-of-manufacture field. Only a clean 4-digit year counts;
// anything else (blank, partial, junk) is null and the lift is priced age-neutral.
export function parseManufactureYear(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d{4}$/.test(trimmed)) return null;
  return Number.parseInt(trimmed, 10);
}

// Parse the number-of-stops field. The minimum is 2 (a lift serves at least two
// levels); blank or junk falls back to the standard 4. Clamped to the engine's
// accepted range so the request always validates server-side.
export function parseStops(raw: string): number {
  const n = Number.parseInt(raw.trim(), 10);
  if (!Number.isFinite(n)) return 4;
  return Math.min(100, Math.max(2, n));
}

// Per-visit price from an annual figure at the chosen frequency, in whole pounds
// (the engine and PDF display whole pounds throughout). Guards a zero/garbage
// frequency rather than returning Infinity/NaN.
export function perVisit(annual: number, visitsYr: number): number {
  if (!Number.isFinite(annual) || !Number.isFinite(visitsYr) || visitsYr <= 0) return 0;
  return Math.round(annual / visitsYr);
}

// Plain-English note about the graduated multi-lift discount. multiDiscount is
// the rate the backend actually applied (0 / 0.05 / 0.15), so the note never
// contradicts the headline total. For a single lift there is nothing to say; for
// two lifts (no discount yet) it nudges toward the next threshold; once a
// discount applies it states the rate.
//
// Session AHC: `discountLiftCount` is the count the band was decided on. When it
// exceeds the lifts on this quote, the band came from the customer's WIDER
// PORTFOLIO (their other sites), and the note has to say so -- it previously
// claimed the discount applied "when all the lifts are signed together", which is
// exactly what stopped being true, and would have had the office believing a
// portfolio band depended on one signature covering every block.
export function multiUnitDiscountNote(numLifts: number, multiDiscount: number, discountLiftCount?: number): string | null {
  const portfolioCount = typeof discountLiftCount === "number" && discountLiftCount > numLifts ? discountLiftCount : null;
  if (numLifts <= 1 && !portfolioCount) return null;
  const pct = Math.round(multiDiscount * 100);
  if (portfolioCount) {
    const liftWord = numLifts === 1 ? "lift" : "lifts";
    return `${numLifts} ${liftWord} here, ${portfolioCount} across this customer's portfolio: ${pct}% multi-lift discount applied to the total.`;
  }
  if (pct > 0) {
    return `${numLifts} lifts: ${pct}% multi-lift discount applied to the total (when all lifts are signed together).`;
  }
  return `${numLifts} lifts. A 3rd lift earns a 5% multi-lift discount, 6 or more earns 15%, applied when all the lifts are signed together.`;
}
